import { useCallback, useMemo, useRef, useState } from 'react'

import { MAX_OPTION_DEPTH } from './select.constants'
import {
  FlattenedOption,
  SelectOptionValue,
  SelectTreeOption,
  UseTreeOptionsParams,
} from './select.types'
import { collectDefaultExpandedKeys, collectExpandedKeys } from './select.utils'

export const useTreeOptions = <V extends SelectOptionValue>({
  filteredOptions,
  originalOptions,
  searchQuery,
}: UseTreeOptionsParams<V>) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<V>>(() =>
    collectDefaultExpandedKeys(originalOptions),
  )
  const prevSearchRef = useRef('')

  // Note: When search query changes, auto-expand ancestors of matching options
  const activeExpandedKeys = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()

    if (!query) {
      // Search cleared — restore manual expanded state
      if (prevSearchRef.current) {
        prevSearchRef.current = ''
      }
      return expandedKeys
    }

    prevSearchRef.current = query
    return collectExpandedKeys(originalOptions, query)
  }, [originalOptions, searchQuery, expandedKeys])

  const toggleExpanded = useCallback((key: V) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }, [])

  const flattenedOptions = useMemo(() => {
    const result: Array<FlattenedOption<V>> = []

    const flatten = (items: Array<SelectTreeOption<V>>, depth: number) => {
      if (depth > MAX_OPTION_DEPTH) return

      for (const item of items) {
        const hasChildren = !!(item.children && item.children.length > 0)

        result.push({ depth, hasChildren, option: item })

        if (hasChildren && activeExpandedKeys.has(item.value)) {
          flatten(item.children!, depth + 1)
        }
      }
    }

    flatten(filteredOptions, 0)
    return result
  }, [filteredOptions, activeExpandedKeys])

  const isExpanded = useCallback((key: V) => activeExpandedKeys.has(key), [activeExpandedKeys])

  return { flattenedOptions, isExpanded, toggleExpanded }
}
